import {Dialog} from '@headlessui/react'
import {useEffect, useState} from 'react'
import {
  NoticeConfig,
  dismissNotice,
  loadNoticeConfig,
  shouldShowNotice,
} from 'utils/config/noticeConfig'

/** 설정을 다시 확인하는 주기. 접속 중인 사용자에게도 새 공지가 닿게 한다. */
const REFRESH_INTERVAL_MS = 300000

interface NoticeModalProps {
  /**
   * 편집 화면 미리보기용. 넘기면 서버를 읽지 않고 이 값을 그대로 그리며,
   * 닫아도 숨김 기록을 남기지 않는다.
   */
  preview?: NoticeConfig
  onPreviewClose?: () => void
}

/**
 * 서버 설정으로 띄우는 공지 팝업.
 *
 * App 최상단에 한 번만 붙는다. 라우트가 바뀌어도 다시 마운트되지 않으므로
 * 페이지를 옮길 때마다 팝업이 다시 뜨는 일은 없다.
 */
const NoticeModal = ({preview, onPreviewClose}: NoticeModalProps) => {
  const [loaded, setLoaded] = useState<NoticeConfig | null>(null)
  const [doNotShowAgain, setDoNotShowAgain] = useState(false)

  useEffect(() => {
    if (preview) return undefined
    let disposed = false

    const check = async () => {
      const config = await loadNoticeConfig()
      if (disposed) return
      setLoaded(shouldShowNotice(config) ? config : null)
    }

    check()
    const interval = setInterval(check, REFRESH_INTERVAL_MS)
    return () => {
      disposed = true
      clearInterval(interval)
    }
  }, [preview])

  const notice = preview ?? loaded

  const handleClose = () => {
    if (!notice) return
    if (preview) {
      onPreviewClose?.()
      return
    }
    dismissNotice(notice, doNotShowAgain)
    setDoNotShowAgain(false)
    setLoaded(null)
  }

  if (!notice) return null

  const {title, body, highlight, allowDismiss} = notice

  return (
    /* 바깥 클릭으로 닫히면 공지를 못 본 채 지나갈 수 있어 확인 버튼으로만 닫는다 */
    <Dialog open onClose={() => {}} className={'relative z-50'}>
      <div className={'fixed inset-0 bg-black/40'} aria-hidden={'true'} />
      <div className={'fixed inset-0 flex items-center justify-center p-4'}>
        <Dialog.Panel
          className={'w-full max-w-[40rem] rounded bg-white p-6 shadow-xl text-center'}
        >
          <Dialog.Title className={'text-3xl font-bold text-gray-900 mb-4'}>{title}</Dialog.Title>

          {body.length > 0 && (
            <Dialog.Description as={'div'} className={'text-gray-800 mb-6 text-xl leading-relaxed'}>
              {body.map((line, index) => (
                /* 문구는 운영자가 자유롭게 넣는 값이라 안정적인 키가 없다 */
                <div key={`notice-line-${index}`}>{line === '' ? <br /> : line}</div>
              ))}
            </Dialog.Description>
          )}

          {highlight && (
            <div
              className={'bg-gray-100 border border-gray-300 rounded-xl p-4 mb-6 text-center'}
            >
              {highlight.label !== '' && (
                <div
                  className={'inline-block bg-blue-600 text-white text-base rounded-xl px-3 py-1 mb-2'}
                >
                  {highlight.label}
                </div>
              )}
              {highlight.date !== '' && (
                <div className={'text-xl font-semibold text-gray-900'}>{highlight.date}</div>
              )}
              {highlight.time !== '' && (
                <div className={'text-xl text-gray-800'}>{highlight.time}</div>
              )}
              {highlight.note !== '' && (
                <div className={'text-gray-700 text-base mt-2'}>{highlight.note}</div>
              )}
            </div>
          )}

          <div className={'flex items-center justify-end gap-4'}>
            {allowDismiss && (
              <div className={'flex items-center gap-2'}>
                <input
                  type={'checkbox'}
                  id={'notice-dont-show-again'}
                  checked={doNotShowAgain}
                  onChange={(e) => setDoNotShowAgain(e.target.checked)}
                  className={'w-5 h-5 cursor-pointer'}
                />
                {/* 확인 버튼 옆에서 눈에 띄어야 하는 선택지라 본문에 준하는 크기로 둔다 */}
                <label
                  htmlFor={'notice-dont-show-again'}
                  className={'text-gray-800 text-lg cursor-pointer'}
                >
                  다음 공지까지 보지 않기
                </label>
              </div>
            )}

            <button
              onClick={handleClose}
              className={'rounded bg-blue-600 px-4 py-2 text-white text-lg hover:bg-blue-700'}
            >
              확인
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default NoticeModal
